/**
 * Utility functions for proper name capitalization
 */

/**
 * Capitalizes the first letter of each word in a name while preserving certain patterns
 * Handles special cases like McDonald, O'Connor, etc.
 * @param name - The name string to capitalize
 * @returns The properly capitalized name
 */
export const capitalizeFirstName = (name: string): string => {
  if (!name || typeof name !== 'string') return name;
  
  // Remove extra spaces and convert to lowercase
  const trimmed = name.trim().toLowerCase();
  if (!trimmed) return trimmed;
  
  // Split by spaces and capitalize each part
  return trimmed
    .split(' ')
    .map(part => {
      if (!part) return part;
      
      // Handle apostrophes (O'Connor, D'Angelo, etc.)
      if (part.includes("'")) {
        return part
          .split("'")
          .map(subpart => subpart.charAt(0).toUpperCase() + subpart.slice(1))
          .join("'");
      }
      
      // Handle hyphens (Mary-Jane, Jean-Claude, etc.)
      if (part.includes('-')) {
        return part
          .split('-')
          .map(subpart => subpart.charAt(0).toUpperCase() + subpart.slice(1))
          .join('-');
      }
      
      // Regular capitalization
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
};

/**
 * Capitalizes last names with special handling for prefixes and compound names
 * @param name - The last name string to capitalize
 * @returns The properly capitalized last name
 */
export const capitalizeLastName = (name: string): string => {
  if (!name || typeof name !== 'string') return name;
  
  // Remove extra spaces and convert to lowercase
  const trimmed = name.trim().toLowerCase();
  if (!trimmed) return trimmed;
  
  // Common prefixes that should remain lowercase (except at the beginning)
  const prefixes = ['de', 'la', 'le', 'du', 'von', 'van', 'der', 'den', 'del'];
  
  // Split by spaces and capitalize each part
  return trimmed
    .split(' ')
    .map((part, index) => {
      if (!part) return part;
      
      // Handle apostrophes (O'Connor, D'Angelo, etc.)
      if (part.includes("'")) {
        return part
          .split("'")
          .map(subpart => subpart.charAt(0).toUpperCase() + subpart.slice(1))
          .join("'");
      }
      
      // Handle hyphens (Smith-Jones, etc.)
      if (part.includes('-')) {
        return part
          .split('-')
          .map(subpart => subpart.charAt(0).toUpperCase() + subpart.slice(1))
          .join('-');
      }
      
      // Handle prefixes (keep lowercase unless it's the first word)
      if (index > 0 && prefixes.includes(part)) {
        return part.toLowerCase();
      }
      
      // Handle Scottish/Irish "Mc" and "Mac" prefixes
      if (part.startsWith('mc') && part.length > 2) {
        return 'Mc' + part.charAt(2).toUpperCase() + part.slice(3);
      }
      if (part.startsWith('mac') && part.length > 3) {
        return 'Mac' + part.charAt(3).toUpperCase() + part.slice(4);
      }
      
      // Regular capitalization
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
};

/**
 * Creates an input handler that automatically capitalizes names as the user types
 * @param updateFunction - The function to call with the capitalized value
 * @param capitalizeFunction - The capitalization function to use (capitalizeFirstName or capitalizeLastName)
 * @returns An input change handler
 */
export const createCapitalizeHandler = (
  updateFunction: (value: string) => void,
  capitalizeFunction: (name: string) => string = capitalizeFirstName
) => {
  return (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const inputType = (e.nativeEvent as InputEvent)?.inputType;
    
    // Don't capitalize if user is deleting (backspace/delete)
    if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
      updateFunction(value);
      return;
    }
    
    // Apply capitalization for other input types
    const capitalizedValue = capitalizeFunction(value);
    updateFunction(capitalizedValue);
  };
};
